
import { Box, Button, Card, CircularProgress, Paper, styled, Typography } from '@mui/material';
import { FC, useContext, useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../contexts/auth/AuthContext';
import useGetWebsiteNotificationRecord from '../hooks/data/useGetWebsiteNotificationRecord';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import getNotificationImage from '../functions/notification';
import { SiteEventType, Species } from '../api/dtos/dtos';
import { grey } from '@mui/material/colors';
import dayjs from 'dayjs';
import Hr from '../components/common/HorizontalRule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const AlertContainer = styled(Box)(() => ({
    display: "flex", 
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "50vh"
}));

const ColumnContainer = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column"
}));

const RowContainer = styled(Box)(() => ({
    display: "flex",
    flexDirection: "row"
}));

const NotificationInformationPage: FC = () => {
    //
    // -- Hooks --
    //
    const { notificationId: notificationId } = useParams();
    const { user, openLogin } = useContext(AuthContext);
    const navigate = useNavigate();

    // notification request
    const notificatioRecordRequest = useMemo(() => {
        return {
            userNotificationId: notificationId ? +notificationId : undefined,
        }
    }, [notificationId]);

    // Get notifcation content
    const { data: notificationData, loading: notificationDataLoading, error }  = useGetWebsiteNotificationRecord(
        notificatioRecordRequest ? notificatioRecordRequest : undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    useEffect(() => {
        console.log(error?.errorCode)
        if (error?.errorCode == "Exception") {
            navigate(`/error?error=${error?.message}`);
        }
        else if (error?.errorCode) {
            navigate(`/error`);
        }
    }, [notificationData, error]);

    if (!user) {
        return (
            <AlertContainer>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        width: "100%",
                        textAlign: "center",
                        borderRadius: 2,
                        background: '#F5F5F5'
                    }}
                >
                    <Typography variant="h5" color="text.primary" gutterBottom>
                        Access Restricted
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        You must be logged in to view this page.
                    </Typography>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => openLogin()}
                        sx={{ color: "white" }}
                    >
                        Log In
                    </Button>
                </Paper>
            </AlertContainer>
        );
    }

    return (
        <ContentWrapper>
            <Typography fontSize={32} color={"#FFFFFF"} sx={{paddingTop: "15px"}}>Notification</Typography> 
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    color: 'white',
                    gap: '8px'
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        color: 'white',
                        fontSize: '0.65rem',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.15)'
                        }
                    }}
                    startIcon={
                        <FontAwesomeIcon
                            icon={faArrowLeft} 
                            style={{ 
                                color: 'white',
                                fontSize: '0.65rem',
                                paddingRight: '3px' 
                            }}
                        />
                    }
                    onClick={() => navigate("../")}
                >
                    Back to Notifications
                </Button>
            </Box>

            <Box style={{ height: '15px' }} />

            <Card
                sx={{ 
                    padding: '20px', 
                    marginBottom: '30px'
                }}
            >
                { notificationDataLoading && (
                    <Box sx={{display: "flex", justifyContent: "center", width: "auto", paddingTop: "20px"}}>
                        <CircularProgress />
                    </Box>
                )}
                { !notificationDataLoading && notificationData && (
                    <>
                        <RowContainer alignItems={'center'}>
                            <img
                                src={getNotificationImage(
                                    notificationData?.userNotification?.species,
                                    notificationData?.userNotification?.siteEventType
                                )}
                                alt=""
                            />
                            <ColumnContainer paddingLeft={'20px'} justifyContent={'center'} width={'100%'}>
                                <Typography 
                                    fontWeight={600} 
                                    fontSize={26}
                                >
                                    {Species[notificationData?.userNotification?.species ?? 0]} - {SiteEventType[notificationData?.userNotification?.siteEventType ?? 0]}
                                </Typography>
                                <Typography 
                                    fontWeight={300} 
                                    fontSize={18} 
                                    color={grey[600]}
                                >
                                    {dayjs(notificationData?.userNotification?.sightingDateEntered).format('hh:mm A - DD/MM/YYYY')}
                                </Typography>
                            </ColumnContainer>
                        </RowContainer>
                        <Hr sx={{ marginY: '10px' }}/>
                        { parse(DOMPurify.sanitize(notificationData?.websiteNotificationRecordDetails ?? "")) }
                    </>
                )}
            </Card>

            <Box style={{ height: '20px' }} />

        </ContentWrapper>
    )
}

export default NotificationInformationPage